import React from 'react';
import { reset, SubmissionError, change } from 'redux-form';
import { push } from 'react-router-redux';
import { gettext } from '@eb/i18n';
import get from 'lodash/get';

import { joinPath } from '@eb/path-utils';
import { setWindowLocation } from '@eb/http';
import { AlertChunky } from '@eb/eds-iconography';

import { parseFormPostData } from '../api/utils';
import { getSetPasswordEvent, notifyParent } from '../utils';
import {
    getLogInFormData,
    getSignUpFormData,
    shouldOnboardingRedirect,
} from './utils';
import {
    lookupUser as lookupUserApi,
    signUpUser as signUpUserApi,
    setPassword as setPasswordApi,
    postAddExternalIdentity,
    setLinkedAccountApi,
} from '../api';

import { logInUser as logInUserApi } from '../api/login';

import {
    LINK_ACCOUNTS_LOG_IN_FORM,
    BASE_URL,
    CHECKOUT_PATH,
    CAPTCHA_FIELD,
    ARGUMENTS_ERROR,
    EXTERNAL_USER_EMAIL_ALREADY_USED_ERROR,
    EXTERNAL_USER_UNKNOWN_ERROR,
    EXTERNAL_USER_WITHOUT_EMAIL_ERROR,
    EMAIL_PERMISSION_DENIED_ERROR,
    TOKEN_NOT_AUTHORIZED_ERROR,
    STAFF_USER_NOT_ALLOWED_ERROR,
    INVALID_SOCIAL_LOGIN_FIELD,
    SOCIAL_LOG_IN_PATH,
    SOCIAL_LOOK_UP_PATH,
    SOCIAL_SIGN_UP_PATH,
    LINK_ACCOUNTS_LOG_IN_PATH,
    EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR,
    SET_LINKED_SOFT_ACCOUNTS_PATH,
    SUSPENDED_ACCOUNT,
    EXTERNAL_UNKNOWN_ERROR,
    SET_LINK_ACCOUNT_OK,
    SET_LINK_ACCOUNT_ERROR,
    SET_LINK_ACCOUNT_TOKEN_EXPIRED,
    SET_LINK_ACCOUNT_OTHER_ERROR,
    USER_ALREADY_HAS_EXTERNAL_PROVIDER,
} from '../constants';

import { GA_EVENTBRITE, GA_APPLE } from '../constants/analytics';

import {
    EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR_MESSAGE,
    LINK_ACCOUNTS_OPTION_FOR_EXTERNAL_USER_EMAIL_ML,
    EMAIL_PERMISSION_DENIED_ERROR_MESSAGE,
    TOKEN_NOT_AUTHORIZED_ERROR_MESSAGE,
    UNKNOWN_ERROR_MESSAGE,
    UNKNOWN_EXTERNAL_PROVIDER_ERROR_MESSAGE,
    LOCKED_ACCOUNT_ERROR_MESSAGE,
    FACEBOOK_ACCOUNT_NOT_LOGGED_IN_ERROR,
    USER_ALREADY_HAS_EXTERNAL_PROVIDER_MESSAGE,
    STAFF_USER_NOT_ALLOWED_LOGIN_MESSAGE,
    PASSWORD_RESET_USED_PASSWORD,
    PASSWORD_TOKEN_EXPIRED_MESSAGE,
    PASSWORD_RESET_RESTRICTED_ACCOUNT,
    GENERIC_ERROR_MESSAGE,
} from '../constants/messages';

import {
    trackLoginClicked,
    trackAccountLinked,
    trackMagicLinkConfirmationClosed,
    trackAccountLinkFailed,
    trackAccountLinkConfirmationClosed,
    trackUseAnotherAccountClicked,
    trackSignUpClicked,
    trackEmailIncorrect,
    trackPasswordIncorrect,
    trackPasswordViewed,
    trackPasswordReseted,
    trackPasswordResetedFailed,
    trackLoginSuccessful,
    trackSocialEmailAlreadyExists,
    trackSuccessfullyLinkAccountsWithExistingEBAccount,
    trackErrorOnLinkAccountsWithExistingEBAccount,
    trackContinueCreateAccountClick,
    trackLoginInsteadClick,
    trackFBLoginNoEmailAddress,
    trackFBLoginEmailNotShared,
    trackFBLoginExternalUserUnknown,
    trackEditEmailEventCheckout,
    trackSignupEvent,
    trackConfirmEmailShowed,
    trackEditEmailMismatch,
    trackFBLoginNotLoggedInCheckout,
    trackSocialSoftAccountExists,
    trackHandleOnErrorMetric,
    trackStaffUserNotAllowed,
} from './gaTracking';

import { handleOnErrorLogin } from './login';

import { handleAppleSocialLogin } from './appleLogin';
import {
    setSocialDataAction,
    setSocialProvider,
    setSocialLinkedAccount,
    setReRequestPermissionStatus,
} from './social';

export const INVALID_PASSWORD_RESET_TOKEN_ACTION = 'invalidToken';
export const invalidPasswordResetToken = () => ({
    type: INVALID_PASSWORD_RESET_TOKEN_ACTION,
    payload: true,
});

export const SET_EMAIL_ACTION = 'setEmail';
export const setEmail = (email) => ({
    type: SET_EMAIL_ACTION,
    payload: email,
});

export const CLEAR_EMAIL_ACTION = 'clearEmail';
export const clearEmail = () => ({
    type: CLEAR_EMAIL_ACTION,
});

export const FORCE_STLD = 'forceStld';
export const forceStld = () => ({
    type: FORCE_STLD,
});

export const SHOW_CAPTCHA = 'showCaptcha';
export const showCaptcha = () => ({
    type: SHOW_CAPTCHA,
});

export const HIDE_CAPTCHA = 'hideCaptcha';
export const hideCaptcha = () => ({
    type: HIDE_CAPTCHA,
});

export const SHOW_AUTH_NOTIFICATION = 'showAuthNotification';
export const showAuthNotification = ({
    message,
    type,
    iconType,
    onClick,
    buttonCTA,
}) => ({
    type: SHOW_AUTH_NOTIFICATION,
    payload: {
        message,
        type,
        iconType,
        onClick,
        buttonCTA,
    },
});

export const CLEAR_AUTH_NOTIFICATION = 'clearAuthNotification';
export const clearAuthNotification = () => ({
    type: CLEAR_AUTH_NOTIFICATION,
});

export const SET_ACCOUNT_EXISTENT = 'setAccountExistent';
export const setAccountExistent = (email) => ({
    type: SET_ACCOUNT_EXISTENT,
    payload: email,
});

export const SHOW_LINKED_ACCOUNTS_DIALOG = 'showLinkedAccountsDialog';
export const showLinkedAccountsDialog = (redirectUrl) => ({
    type: SHOW_LINKED_ACCOUNTS_DIALOG,
    payload: {
        redirectUrl,
    },
});

export const SET_IS_LOADING = 'SET_IS_LOADING';
export const setIsLoading = (isLoading) => ({
    type: SET_IS_LOADING,
    payload: isLoading,
});

export const SET_SHOW_MODAL = 'SET_SHOW_MODAL';
export const setShowModal = (show) => ({
    type: SET_SHOW_MODAL,
    payload: show,
});

export const UPDATE_CURRENT_FORM_TYPE_ACTION =
    'UPDATE_CURRENT_FORM_TYPE_ACTION';
export const updateCurrentFormType = (data) => ({
    type: UPDATE_CURRENT_FORM_TYPE_ACTION,
    payload: {
        formType: data,
    },
});

export const EXISTING_ACCOUNT_CHECK = 'existingAccountCheckAction';
export const existingAccountCheck = (data) => (dispatch) => {
    dispatch({
        type: 'existingAccountCheckAction',
        payload: data,
    });
};

export const EXISTING_SOFT_ACCOUNT_CHECK = 'existingSoftAccountCheckAction';
export const existingSoftAccountCheck = (data) => (dispatch) => {
    dispatch({
        type: EXISTING_SOFT_ACCOUNT_CHECK,
        payload: data,
    });
};

export const updateFormType = (path = '') => (dispatch) => {
    //Any time we change form want to reset our captcha display logic.
    //Decision service will decide if it will be presented on submission.
    dispatch(hideCaptcha());
    dispatch(push(joinPath(BASE_URL, path)));
};

export const updateCheckoutFormType = (path = '') => (dispatch) => {
    //Any time we change form want to reset our captcha display logic.
    //Decision service will decide if it will be presented on submission.
    dispatch(hideCaptcha());
    dispatch(updateCurrentFormType(path));
    dispatch(push(joinPath(BASE_URL, CHECKOUT_PATH, path)));
};

export const cleanSocialAuthenticationData = () => (dispatch) => {
    //Any time we want to reset the social authentication data we dispatch this action.
    dispatch(setSocialProvider(''));
};

export const getEmailFromState = (getState) => {
    const state = getState();
    const {
        authentication: { email } = {},
        social: {
            socialData: { apple: { email: _socialEmail } = {} },
        },
    } = state;

    return email || _socialEmail || '';
};

export const handleOnError = (dispatch, state, data, actionCategory = null) => (
    error,
) => {
    const gaActionCategory = actionCategory ? actionCategory : null;

    const {
        social: { socialProvider, socialData },
    } = state;

    if (error.name === 'CAPTCHA_REQUIRED') {
        //we need to dispatch an action to handle displaying the captcha, so this specific error
        //lives slightly outside of the standard error protocol. Have it here because proxying the
        //specific error for requiring the captcha to this point is cleaner than inserting dispatch
        //directly into the response handling itself
        dispatch(showCaptcha());
        trackHandleOnErrorMetric({
            state,
            message: error.name,
            label: socialProvider,
        });
        //once we are here in the response handling and have access to dispatch, we need to continue
        //passing along the error to redux form such that its lifecycle methods correctly represent the
        //failed submission state. We don't want to actually display a form error, as at this stage
        //showing the captcha
        throw new SubmissionError({
            [CAPTCHA_FIELD]: gettext('Complete to proceed'),
        });
    }

    // Scenario: facebookEmailDenied
    if (error.name === EXTERNAL_USER_EMAIL_ALREADY_USED_ERROR) {
        // dispatch action to show notification here
        // so the error can be seen on the screen

        // Merge current social data with possible changes made by user
        const updatedSocialData = { ...socialData, ...data };

        dispatch(
            showAuthNotification({
                message: LINK_ACCOUNTS_OPTION_FOR_EXTERNAL_USER_EMAIL_ML,
                iconType: null,
            }),
        );

        trackSocialEmailAlreadyExists({
            sourceProvider: socialProvider,
            state,
        });

        /*
        The subsequent routes (login/signup/etc) expect email to be in state, and the
        account status to be checked, so need to update store before dealing with the response
        logic. When setting account status as checked, the email reducer is
        also listening and will update.
        */
        if (updatedSocialData && updatedSocialData.email) {
            dispatch(setAccountExistent(updatedSocialData.email));
        }
        if (updatedSocialData) {
            dispatch(setSocialDataAction(updatedSocialData, socialProvider));
        }

        dispatch(updateFormType(LINK_ACCOUNTS_LOG_IN_PATH));
        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    // Scenario: facebookSoftAccount
    if (error.name === EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR) {
        dispatch(
            showAuthNotification({
                message: EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );

        trackSocialSoftAccountExists({
            state,
            sourceProvider: socialProvider,
        });

        if (data && data.email && socialProvider != GA_APPLE) {
            dispatch(setAccountExistent(data.email));
            dispatch(setSocialDataAction(data, socialProvider));
        }
        dispatch(updateFormType(SET_LINKED_SOFT_ACCOUNTS_PATH));

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    // Scenario: facebookEmailNotProvided
    if (error.name === EXTERNAL_USER_WITHOUT_EMAIL_ERROR) {
        // dispatch action to redirect to social sign up
        // using data brought from external source
        if (data && (data.firstName || data.lastName)) {
            dispatch(setSocialDataAction(data, socialProvider));
        }

        dispatch(updateFormType(SOCIAL_SIGN_UP_PATH));

        trackFBLoginNoEmailAddress({ state });

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    // Scenario: facebookEmailDenied
    if (error.name === EMAIL_PERMISSION_DENIED_ERROR) {
        // dispatch action that makese the FacebookLoginComponent re request permissions.
        dispatch(setReRequestPermissionStatus());

        // dispatch action to show notification here
        // so the error can be seen on the screen
        dispatch(
            showAuthNotification({
                message: EMAIL_PERMISSION_DENIED_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );

        trackFBLoginEmailNotShared({ state });

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    // Scenario: staffUserNotAllowed
    if (error.name === STAFF_USER_NOT_ALLOWED_ERROR) {
        // dispatch action to show notification here
        // so the error can be seen on the screen
        dispatch(
            showAuthNotification({
                message: STAFF_USER_NOT_ALLOWED_LOGIN_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );

        trackStaffUserNotAllowed({ state, sourceProvider: socialProvider });

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    // Scenario: facebookUnknownUser
    if (error.name === EXTERNAL_USER_UNKNOWN_ERROR) {
        // dispatch action to redirect to social sign up
        // using data brought from external source
        let formType = SOCIAL_SIGN_UP_PATH;
        let message = null;

        trackFBLoginExternalUserUnknown({ state });

        lookupUserApi(data.email).then(({ exists, can_login: canLogin }) => {
            // If user exists, skip confirmation view
            if (exists) {
                // If user has full account, redirect to link accounts view
                if (canLogin) {
                    message = LINK_ACCOUNTS_OPTION_FOR_EXTERNAL_USER_EMAIL_ML;
                    formType = LINK_ACCOUNTS_LOG_IN_PATH;
                    // If user has soft account, redirect to create password view
                } else {
                    message = EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR_MESSAGE;
                    formType = SET_LINKED_SOFT_ACCOUNTS_PATH;
                }
                dispatch(setAccountExistent(data.email));
                dispatch(
                    showAuthNotification({
                        message,
                        iconType: <AlertChunky />,
                    }),
                );
            }

            dispatch(setSocialDataAction(data, socialProvider));
            dispatch(updateFormType(formType));
        });

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    // Scenario: facebookTokenNotAuthorized
    if (error.name === TOKEN_NOT_AUTHORIZED_ERROR) {
        // dispatch action to show notification here
        // so the error can be seen on the screen
        dispatch(
            showAuthNotification({
                message: TOKEN_NOT_AUTHORIZED_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
        trackHandleOnErrorMetric({
            state,
            message: error.name,
            label: socialProvider,
        });

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    // Scenario: facebookInvalidField
    if (error.name === INVALID_SOCIAL_LOGIN_FIELD) {
        // dispatch action to show notification here
        // so the error can be seen on the screen
        // it shows the unknown error message
        dispatch(
            showAuthNotification({
                message: UNKNOWN_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
        trackHandleOnErrorMetric({
            state,
            message: error.name,
            label: socialProvider,
        });

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    // Scenario: facebookAlreadyLinked
    if (error.name === USER_ALREADY_HAS_EXTERNAL_PROVIDER) {
        dispatch(
            showAuthNotification({
                message: USER_ALREADY_HAS_EXTERNAL_PROVIDER_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
        trackHandleOnErrorMetric({
            state,
            message: error.name,
            label: socialProvider,
        });
        return Promise.resolve();
    }

    if (error.name === SUSPENDED_ACCOUNT) {
        // dispatch action to show notification here
        // so the error can be seen on the screen
        // it shows the unknown error message
        dispatch(
            showAuthNotification({
                message: LOCKED_ACCOUNT_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
        trackHandleOnErrorMetric({
            state,
            message: error.name,
            label: socialProvider,
        });

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    if (error.name === ARGUMENTS_ERROR) {
        dispatch(
            showAuthNotification({
                message: UNKNOWN_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
        trackHandleOnErrorMetric({
            state,
            message: error.name,
            label: socialProvider,
        });
        return Promise.resolve();
    }

    if (error.errors && error.errors.password) {
        trackPasswordIncorrect({
            gaActionCategory,
            sourceProvider: socialProvider,
            state,
        });

        // throw the error because is related with
        // the password form field
        return Promise.reject(error);
    }

    //handle email error
    if (error.errors && error.errors.email) {
        trackEmailIncorrect({
            gaActionCategory,
            sourceProvider: socialProvider,
            state,
        });

        // throw the error because is related with
        // the email form field
        return Promise.reject(error);
    }

    if (error.errors && error.errors.type === EXTERNAL_UNKNOWN_ERROR) {
        trackHandleOnErrorMetric({
            state,
            message: error.errors.type,
            label: socialProvider,
        });
        dispatch(
            showAuthNotification({
                message: UNKNOWN_EXTERNAL_PROVIDER_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );

        return Promise.resolve();
    }

    // Unknown error, let the user know and reject the promise
    trackHandleOnErrorMetric({
        state,
        message: error.name,
        label: socialProvider,
    });
    dispatch(
        showAuthNotification({
            message: UNKNOWN_ERROR_MESSAGE,
            iconType: <AlertChunky />,
        }),
    );

    return Promise.reject(error);
};

export const submitSuccessRedirect = (url) => () => setWindowLocation(url);

export const moveBack = () => (dispatch, getState) => {
    const state = getState();
    const {
        routing: { locationBeforeTransitions },
        social: { socialProvider },
    } = state;

    if (
        locationBeforeTransitions &&
        locationBeforeTransitions.pathname ===
            `${BASE_URL}/${SOCIAL_LOG_IN_PATH}`
    ) {
        dispatch(updateFormType(SOCIAL_LOOK_UP_PATH));
    } else {
        dispatch(cleanSocialAuthenticationData());
        dispatch(updateFormType());
    }
    trackUseAnotherAccountClicked({
        sourceProvider: socialProvider,
        state,
    });
};

export const moveBackCheckout = () => (dispatch, getState) => {
    const state = getState();
    const {
        social: { socialProvider: sourceProvider },
    } = state;

    dispatch(updateCheckoutFormType());

    trackEditEmailEventCheckout({ state, sourceProvider });
};

export const signUpUser = (data) => (dispatch, getState) => {
    const state = getState();

    return signUpUserApi(parseFormPostData(getSignUpFormData(data, getState)))
        .then((response) => {
            let redirectUrl = response['redirect_url'];

            if (shouldOnboardingRedirect(state)) {
                redirectUrl = `/u/interests/?onboarding_referrer=${redirectUrl}`;
            }
            return dispatch(submitSuccessRedirect(redirectUrl));
        })
        .then(() => {
            trackSignupEvent({ state });
            return trackLoginSuccessful({
                sourceProvider: GA_EVENTBRITE,
                state,
            });
        })
        .catch(handleOnError(dispatch, state));
};

/**
 * Called when:
 *  you are linking your account at the same time you are logging in using Magic Link Screen
 */
export const linkAccountsOnLogin = (data) => (dispatch, getState) => {
    const state = getState();
    const {
        social: { socialProvider, socialData },
    } = state;

    const accessToken = socialData[socialProvider]?.accessToken;

    dispatch(hideCaptcha());
    dispatch(change(LINK_ACCOUNTS_LOG_IN_FORM, CAPTCHA_FIELD, null));

    return logInUserApi({
        data: parseFormPostData(getLogInFormData(data, getState)),
    })
        .then((response) =>
            postAddExternalIdentity('me', socialProvider, accessToken)
                .then(() => {
                    if (global.window && window.localStorage) {
                        localStorage.removeItem('referrer');
                    }

                    trackSuccessfullyLinkAccountsWithExistingEBAccount({
                        sourceProvider: socialProvider,
                        state,
                    });

                    trackLoginSuccessful({
                        sourceProvider: socialProvider,
                        state,
                    });

                    dispatch(
                        showLinkedAccountsDialog(response['redirect_url']),
                    );
                })
                .catch((error) => {
                    trackErrorOnLinkAccountsWithExistingEBAccount({
                        errorDescription: get(
                            error,
                            'extraData.parsedError.error',
                            get(
                                error,
                                'extraData.response.statusText',
                                'Error not defined',
                            ),
                        ),
                        state,
                    });
                    trackLoginSuccessful({
                        sourceProvider: socialProvider,
                        state,
                    });
                    dispatch(submitSuccessRedirect(response['redirect_url']));
                }),
        )
        .catch(handleOnErrorLogin(dispatch, state));
};

export const linkAccountOnNewUser = (data) => (dispatch, getState) => {
    return handleAppleSocialLogin({
        data: {
            accessToken: data.externalToken,
            sourceProvider: data.externalProvider,
            email: data.email,
        },
        getState,
        dispatch,
    });
};

export const setPassword = (data) => (dispatch, getState) => {
    const state = getState();
    const {
        status: { tosSocialAccepted },
        routing: {
            locationBeforeTransitions: { pathname: currentRoute },
        },
    } = state;
    const setPasswordEvent = getSetPasswordEvent(currentRoute);
    const { external_provider } = data;

    // Only add this extra parameter when user accept ToS by Popup in new design.
    if (tosSocialAccepted) {
        data['accepted_tos_by_popup'] = 1;
    }

    // Coming from the backend
    const sourceProvider =
        external_provider === 'None' ? GA_EVENTBRITE : external_provider;

    return setPasswordApi(data)
        .then((response) =>
            dispatch(submitSuccessRedirect(response['redirect_url'])),
        )
        .then(() => {
            trackPasswordReseted({ sourceProvider, state, setPasswordEvent });
            trackLoginSuccessful({ sourceProvider, state });
        })
        .catch((error) => {
            const errorCode = get(
                error,
                'extraData.response.error',
                get(error, 'errors._error'),
            );
            let errorMessage = GENERIC_ERROR_MESSAGE;
            switch (errorCode) {
                case 'PASSWORD_USED':
                    errorMessage = PASSWORD_RESET_USED_PASSWORD;
                    break;
                case 'INVALID_HASH':
                    errorMessage = PASSWORD_TOKEN_EXPIRED_MESSAGE;
                    break;
                case 'RESTRICTED_ACCOUNT':
                    errorMessage = PASSWORD_RESET_RESTRICTED_ACCOUNT;
                    break;
            }

            trackPasswordResetedFailed({
                errorMessage: errorCode,
                state,
                setPasswordEvent,
            });

            dispatch(
                showAuthNotification({
                    message: errorMessage,
                }),
            );
        });
};

export const setLinkedAccount = (query) => (dispatch, getState) => {
    const internalToken = query['h'];
    const sourceProvider = query['external_provider'];
    const state = getState();
    dispatch(setIsLoading(true));

    return setLinkedAccountApi(internalToken)
        .then((response) => {
            let apiResult = SET_LINK_ACCOUNT_OK;
            const { error_link_account } = response;
            if (error_link_account == true) {
                // Error on link account but user is logged
                apiResult = SET_LINK_ACCOUNT_OTHER_ERROR;
            }
            dispatch(
                setSocialLinkedAccount({
                    apiResult,
                }),
            );
            const linked = !error_link_account;
            trackAccountLinked({ sourceProvider, state, linked });
            trackLoginSuccessful({ sourceProvider, state });
            dispatch(setIsLoading(false));
        })
        .catch((error) => {
            let apiResult = SET_LINK_ACCOUNT_ERROR;
            let errorDescription = get(
                error,
                'response.parsedError.error',
                get(error, 'response.statusText', 'Error not defined'),
            );
            const { status } = error.response;

            if (status === 403) {
                // Token Expired
                apiResult = SET_LINK_ACCOUNT_TOKEN_EXPIRED;
                errorDescription = SET_LINK_ACCOUNT_TOKEN_EXPIRED;
            }
            errorDescription = status + ' - ' + errorDescription;
            dispatch(
                setSocialLinkedAccount({
                    apiResult,
                }),
            );
            trackAccountLinkFailed({ errorDescription, state });
            dispatch(setIsLoading(false));
        });
};

export const closeButtonClicked = (label) => (dispatch, getState) => {
    const state = getState();
    trackAccountLinkConfirmationClosed({ state, label });
};

// Do not get the social provider from the store here, it may not be defined yet.
export const trackSocialProviderClick = ({
    socialProvider,
    stateFromProvider,
}) => (dispatch, getState) => {
    const state = getState();

    trackLoginClicked({
        sourceProvider: socialProvider,
        stateFromProvider,
        state,
    });
};

/**
 * Seems like this one is not used
 * @deprecated
 */
export const trackSocialProviderCreateAccount = ({ socialProvider }) => (
    dispatch,
    getState,
) => {
    const state = getState();

    trackContinueCreateAccountClick({
        sourceProvider: socialProvider,
        state,
    });
};

export const trackEmailConfirmationInput = () => (dispatch, getState) => {
    const state = getState();
    trackConfirmEmailShowed({ state });
};

export const trackEmailMismatchAction = () => (dispatch, getState) => {
    const state = getState();
    trackEditEmailMismatch({ state });
};

export const onCheckoutSignInHeightChange = (componentHeight) => (dispatch) => {
    notifyParent('checkoutLoginHeightChange', {
        checkoutLoginHeight: componentHeight,
    });
};
/**
 * Function to handle when a user email only has a FB soft account, but they are not
 * logged into facebook in the browser
 */
export const fbAccountNotConnected = () => (dispatch, getState) => {
    const state = getState();
    trackFBLoginNotLoggedInCheckout({ state });

    dispatch(
        showAuthNotification({
            message: FACEBOOK_ACCOUNT_NOT_LOGGED_IN_ERROR,
            iconType: <AlertChunky />,
        }),
    );
    notifyParent('checkoutLoginSoftAccount');
};

export const redirectToSignUp = (label = GA_EVENTBRITE) => (
    dispatch,
    getState,
) => {
    const state = getState();
    trackSignUpClicked({ state, label });
    dispatch(clearEmail());
    dispatch(clearAuthNotification());
    dispatch(push(joinPath(BASE_URL, '/signin/signup')));
};

export const redirectToLogIn = () => (dispatch, getState) => {
    const state = getState();
    trackLoginInsteadClick({ state });
    dispatch(clearAuthNotification());
    dispatch(push(joinPath(BASE_URL, '/signin')));
};

export const viewPassword = () => (dispatch, getState) => {
    const state = getState();
    const {
        social: { socialProvider },
    } = state;

    trackPasswordViewed({ sourceProvider: socialProvider, state });
};
export const closeMagicLinkModal = () => (dispatch, getState) => {
    const state = getState();
    const {
        social: { socialProvider },
    } = state;

    trackMagicLinkConfirmationClosed({ sourceProvider: socialProvider, state });
};
//Action to reset Form fields
export const resetForm = (formName) => (dispatch) => {
    dispatch(clearAuthNotification());
    dispatch(reset(formName));
};
